import type { ReactNode } from 'react';

import type {
	ProductId,
	ProductType,
	ReviewScoreDistribution,
	VariantId,
} from 'models/product';

import { ProductPrintContextProvider } from './ProductPrintContextProvider';
import { ProductQuestionsContextProvider } from './ProductQuestionsContextProvider';
import {
	AccessoriesContextProvider,
	BelongsToBundleContextProvider,
	BelongsToContextProvider,
	BundleContextProvider,
	SparePartsContextProvider,
} from './RelatedProductsContextProvider';
import { ReviewContextProvider } from './ReviewContextProvider';
import { SimilarProductsContextProvider } from './SimilarProductsContextProvider';
import { StockContextProvider } from './StockContextProvider';

interface Props {
	children: ReactNode;
	hasAccessories: boolean;
	hasBelongsTo: boolean;
	hasBelongsToBundle: boolean;
	hasSpareParts: boolean;
	isCustomizable: boolean;
	isSellable: boolean;
	productId: ProductId;
	productType: ProductType;
	reviewScore: ReviewScoreDistribution | undefined;
	variantId: VariantId;
	variantIds?: VariantId[] | undefined;
}

export default function ProductDetailsContexts({
	children,
	hasAccessories,
	hasBelongsTo,
	hasBelongsToBundle,
	hasSpareParts,
	isCustomizable,
	isSellable,
	productId,
	productType,
	reviewScore,
	variantId,
	variantIds,
}: Props) {
	return (
		<SimilarProductsContextProvider productId={productId}>
			<AccessoriesContextProvider
				hasAccessories={hasAccessories}
				variantId={variantId}
			>
				<BelongsToContextProvider
					hasBelongsTo={hasBelongsTo}
					variantId={variantId}
					isInitialOpen={productType === 'SparePart'}
				>
					<BelongsToBundleContextProvider
						hasBelongsToBundle={hasBelongsToBundle}
						variantId={variantId}
					>
						<BundleContextProvider
							hasBundle={productType === 'Bundle'}
							variantId={variantId}
						>
							<SparePartsContextProvider
								hasSpareParts={hasSpareParts}
								variantId={variantId}
							>
								<ProductPrintContextProvider
									isCustomizable={isCustomizable}
									isSellable={isSellable}
									variantId={variantId}
								>
									<StockContextProvider
										variantId={variantId}
										variantIds={variantIds}
										isSellable={isSellable}
									>
										<ReviewContextProvider
											variantId={variantId}
											productType={productType}
											reviewScore={reviewScore}
										>
											<ProductQuestionsContextProvider
												variantId={variantId}
												isActive={productType === 'Product'}
											>
												{children}
											</ProductQuestionsContextProvider>
										</ReviewContextProvider>
									</StockContextProvider>
								</ProductPrintContextProvider>
							</SparePartsContextProvider>
						</BundleContextProvider>
					</BelongsToBundleContextProvider>
				</BelongsToContextProvider>
			</AccessoriesContextProvider>
		</SimilarProductsContextProvider>
	);
}
ProductDetailsContexts.displayName = 'ProductDetailsContexts';
